import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import { useEffect, useRef } from "react";
import { Col, Container, Row } from "react-bootstrap";
import {
  FaArrowLeft,
  FaArrowRight,
  FaCalendarCheck,
  FaPlane,
  FaRegClock,
  FaRoute,
  FaUserFriends,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import { useLanguage } from "./LanguageProvider";

gsap.registerPlugin(ScrollTrigger);

const FlightSightseeingHome = () => {
  const { currentLang } = useLanguage();
  const sectionRef = useRef(null);
  const isAR = currentLang === "ar";

  const content = {
    EN: {
      title: "About Flight Sightseeing Tour",
      subtitle: "Cairo Sightseeing ZLIN Airplane",
      desc: "Experience Egypt's wonders in unparalleled style aboard our meticulously maintained Zlin aircraft or opt for the exhilarating adventure of our helicopters. Soar over Cairo and the Pyramids, where our expert pilots provide a bird's-eye view of these iconic landmarks. Revel in the magic of exclusive landings near the Pyramids, offering you a chance to step onto historic grounds and immerse yourself in their grandeur. Extend your gaze to the stunning landscapes of Hurgada, Gouna, and Luxor. Our Flight Sightseeing Tour captures the essence of Egypt's diverse beauty, from Cairo's bustling urbanity to the serene Red Sea coast and the historical richness of Luxor. Perfect for romantic dates, birthdays, and real estate tours, allowing VIP clients and investors to view locations and development progress, especially in areas like the New Administrative Capital.",
      stats: [
        { icon: <FaRoute />, text: "Route: Over Cairo & Pyramids" },
        {
          icon: <FaUserFriends />,
          text: "Seats: 3 Passengers (or 2 adults & 2 kids)",
        },
        { icon: <FaRegClock />, text: "Duration: 40 Min" },
        { icon: <FaPlane />, text: "Flying Altitude: 1000 FT" },
        {
          icon: <FaCalendarCheck />,
          text: "Reservation 48 hrs. Before flight",
        },
      ],
      btn: "Book Now",
      pyramidsTitle: "Pyramids Landing",
      pyramidsDesc:
        "The exhilarating adventure of our helicopters. Soar over Cairo and the Pyramids, where our expert pilots provide a bird's-eye view of these iconic landmarks. Revel in the magic of exclusive landings near the Pyramids, offering you a chance to step onto historic grounds and immerse yourself in their grandeur.",
      discover: "Discover Egypt's Wonders With FlyVia",
    },
    AR: {
      title: "عن جولات الطيران السياحية",
      subtitle: "جولات القاهرة بطائرة ZLIN",
      desc: "اكتشف عجائب مصر بأسلوب لا مثيل له على متن طائرات Zlin الخاصة بنا أو اختر المغامرة المثيرة عبر مروحياتنا. حلق فوق القاهرة والأهرامات حيث يقدم طيارونا الخبراء إطلالة بانورامية لهذه المعالم. استمتع بسحر الهبوط الحصري بالقرب من الأهرامات لتغوص في عظمتها التاريخية. جولاتنا تشمل أيضًا مناظر الغردقة، الجونة، والأقصر. مثالية للمناسبات الرومانسية، أعياد الميلاد، والجولات العقارية للمستثمرين لرؤية التطورات في العاصمة الإدارية الجديدة.",
      stats: [
        { icon: <FaRoute />, text: "المسار: فوق القاهرة والأهرامات" },
        {
          icon: <FaUserFriends />,
          text: "المقاعد: 3 ركاب (أو شخصين بالغين وطفلين)",
        },
        { icon: <FaRegClock />, text: "المدة: 40 دقيقة" },
        { icon: <FaPlane />, text: "ارتفاع الطيران: 1000 قدم" },
        { icon: <FaCalendarCheck />, text: "الحجز: قبل الرحلة بـ 48 ساعة" },
      ],
      btn: "احجز الآن",
      pyramidsTitle: "الهبوط عند الأهرامات",
      pyramidsDesc:
        "المغامرة المثيرة عبر مروحياتنا. حلق فوق القاهرة والأهرامات حيث يقدم طيارونا إطلالة بانورامية. استمتع بالهبوط الحصري بالقرب من الأهرامات وانغمس في عظمتها التاريخية.",
      discover: "اكتشف عجائب مصر مع FlyVia",
    },
  };

  const t = content[currentLang.toUpperCase()] || content.EN;

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Main Text Elements Animation
      gsap.from(".fs-text-anim", {
        y: 40,
        opacity: 0,
        duration: 1,
        stagger: 0.15,
        ease: "power4.out",
        scrollTrigger: {
          trigger: ".fs-section",
          start: "top 75%",
        },
      });

      // Stats Cards Cascading Animation
      gsap.from(".fs-stat-item", {
        x: isAR ? 60 : -60,
        opacity: 0,
        duration: 0.8,
        stagger: 0.12,
        ease: "power3.out",
        scrollTrigger: {
          trigger: ".fs-stats-card",
          start: "top 80%",
        },
      });

      // Image & Showcase Reveal Animation
      gsap.from(".fs-showcase-img", {
        scale: 0.95,
        opacity: 0,
        duration: 1.2,
        ease: "power2.out",
        scrollTrigger: {
          trigger: ".fs-showcase-section",
          start: "top 75%",
        },
      });
    }, sectionRef);

    return () => ctx.revert();
  }, [currentLang, isAR]);

  // Utility to style titles neatly without relying on strict, error-prone string splitting
  const highlightTitle = (string, highlightIndex = 0) => {
    const words = string.split(" ");
    return words.map((word, i) => (
      <span
        key={i}
        className={i === highlightIndex ? "text-primary-gradient" : ""}
      >
        {word}{" "}
      </span>
    ));
  };

  return (
    <div ref={sectionRef} className="fs-section" dir={isAR ? "rtl" : "ltr"}>
      <style>{`
        :root {
          --primary-gradient: linear-gradient(135deg, #e52d27 0%, #b31217 100%);
          --dark-overlay: linear-gradient(to bottom, rgba(10, 12, 16, 0.96), rgba(15, 20, 28, 0.85), rgba(10, 12, 16, 0.98));
          --glass-bg: rgba(255, 255, 255, 0.02);
          --glass-border: rgba(255, 255, 255, 0.07);
          --text-muted: #94a3b8;
        }

        .fs-section {
          position: relative;
          color: #ffffff;
          padding: 140px 0;
          overflow: hidden;
          background-color: #0a0c10;
          border-radius: 60px 60px 0 0;
          margin-top: -40px;
          z-index: 10;
        }

        .fs-video-bg {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          z-index: 0;
        }

        .fs-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.4);
          z-index: 1;
        }

        .fs-wrapper {
          position: relative;
          z-index: 2;
        }

        .fs-tagline {
          font-size: 14px;
          text-transform: uppercase;
          letter-spacing: 2px;
          color: #e52d27;
          font-weight: 700;
          margin-bottom: 15px;
          display: block;
        }

        .fs-title {
          font-size: clamp(32px, 4.5vw, 52px);
          font-weight: 800;
          line-height: 1.2;
          margin-bottom: 25px;
          letter-spacing: -0.5px;
        }

        .text-primary-gradient {
          background: var(--primary-gradient);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .fs-desc {
          color: var(--text-muted);
          font-size: clamp(16px, 1.2vw, 18px);
          line-height: 1.8;
          margin-bottom: 40px;
        }

        /* Modern CTA Button styling */
        .fs-cta-btn {
          background: var(--primary-gradient);
          color: #fff;
          border: none;
          padding: 16px 38px;
          border-radius: 50px;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          gap: 12px;
          text-decoration: none;
          transition: all 0.3s ease;
          box-shadow: 0 10px 25px rgba(229, 45, 39, 0.25);
        }

        .fs-cta-btn:hover {
          transform: translateY(-3px);
          color: #fff;
          box-shadow: 0 15px 30px rgba(229, 45, 39, 0.4);
        }

        .fs-btn-icon {
          transition: transform 0.3s ease;
        }

        .fs-cta-btn:hover .fs-btn-icon {
          transform: translateX(${isAR ? "-5px" : "5px"});
        }

        /* Feature Glass Box styling */
        .fs-stats-card {
          background: var(--glass-bg);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid var(--glass-border);
          border-radius: 30px;
          padding: 40px;
          height: 100%;
          box-shadow: 0 40px 80px -20px rgba(0, 0, 0, 0.5);
        }

        .fs-stats-card-title {
          font-size: 22px;
          font-weight: 700;
          margin-bottom: 30px;
          border-bottom: 1px solid var(--glass-border);
          padding-bottom: 15px;
        }

        .fs-stat-item {
          display: flex;
          align-items: center;
          gap: 20px;
          padding: 16px 0;
          border-bottom: 1px solid rgba(255, 255, 255, 0.04);
        }

        .fs-stat-item:last-child {
          border-bottom: none;
          padding-bottom: 0;
        }

        .fs-icon-box {
          background: rgba(255, 255, 255, 0.03);
          color: #e52d27;
          width: 48px;
          height: 48px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          border: 1px solid rgba(255, 255, 255, 0.05);
          flex-shrink: 0;
        }

        .fs-stat-text {
          font-size: 16px;
          color: #f1f5f9;
          font-weight: 500;
        }

        /* Image Section Styling */
        .fs-showcase-section {
          margin-top: 140px;
        }

        .fs-img-container {
          position: relative;
          border-radius: 28px;
          overflow: hidden;
          box-shadow: 0 50px 100px -30px rgba(0, 0, 0, 0.7);
        }

        .fs-img-container::after {
          content: '';
          position: absolute;
          inset: 0;
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 28px;
          pointer-events: none;
        }

        .fs-showcase-img {
          width: 100%;
          height: auto;
          object-fit: cover;
          transition: transform 0.7s ease;
        }

        .fs-img-container:hover .fs-showcase-img {
          transform: scale(1.03);
        }

        /* Banner styling */
        .fs-discover-banner {
          text-align: center;
          margin-top: 120px;
          padding: 60px 40px;
          background: radial-gradient(circle at center, rgba(229, 45, 39, 0.1) 0%, transparent 70%);
          border-radius: 30px;
        }

        .fs-discover-text {
          font-size: clamp(24px, 3.5vw, 40px);
          font-weight: 800;
          letter-spacing: -0.5px;
        }
      `}</style>

      {/* Hero Video Background Elements */}
      <video autoPlay loop muted playsInline className="fs-video-bg">
        <source src="/flight.mp4" type="video/mp4" />
      </video>
      <div className="fs-overlay"></div>

      <Container className="fs-wrapper">
        <Row className="gy-5 align-items-stretch">
          <Col lg={7} className="d-flex flex-column justify-content-center">
            <span className="fs-tagline fs-text-anim">
              {isAR ? "تجربة حصرية" : "Exclusive Experience"}
            </span>
            <h1 className="fs-title fs-text-anim">
              {highlightTitle(t.title, isAR ? 1 : 1)}
            </h1>

            <p className="fs-desc fs-text-anim !text-[rgba(255,255,255,0.8)]">
              {t.desc}
            </p>
            <div className="fs-text-anim">
              <Link to="/contact" className="fs-cta-btn">
                <span>{t.btn}</span>
                <span className="fs-btn-icon">
                  {isAR ? <FaArrowLeft /> : <FaArrowRight />}
                </span>
              </Link>
            </div>
          </Col>

          <Col lg={5}>
            <div className="fs-stats-card">
              <h3 className="fs-stats-card-title text-white">{t.subtitle}</h3>
              <div className="fs-stats-list">
                {t.stats.map((stat, i) => (
                  <div className="fs-stat-item" key={i}>
                    <div className="fs-icon-box">{stat.icon}</div>
                    <div className="fs-stat-text">{stat.text}</div>
                  </div>
                ))}
              </div>
            </div>
          </Col>
        </Row>

        {/* Feature Split ShowCase section */}
        <Row className="fs-showcase-section align-items-center gy-1">
          <Col lg={6} className={isAR ? "order-lg-2" : "order-lg-1"}>
            <div className="fs-img-container">
              <img
                src="/4460dff2-298b-4bd6-aa84-58ad35651c2b.jfif"
                alt="Pyramids Landing view"
                className="fs-showcase-img"
              />
            </div>
          </Col>
          <Col
            lg={6}
            className={`px-lg-5 ${isAR ? "order-lg-1" : "order-lg-2"}`}
          >
            <h2 className="fs-title fs-text-anim">
              {highlightTitle(t.pyramidsTitle, 0)}
            </h2>
            <p className="fs-desc fs-text-anim !text-[rgba(255,255,255,0.8)]">
              {t.pyramidsDesc}
            </p>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default FlightSightseeingHome;
